// 归来结算规划（P6-POLISH-ENG-002 / 设计规格 design/ux/07-polish-ux-spec.md §B）。
//
// **本文件是 `roll.ts` 之上的一层包装器，`roll.ts` 一行不改。**
// 理由：`roll.ts` 是 GDD 01 「永不空手」红线的载体（10,000 次仿真、A1 断言 giftIds.length ≥ 1、
// giftIds.length === 0 时兜底重抽）。老板已拍板允许空手归来，但那条红线断言在 roll 层必须继续成立 ——
// 空手是**归来策略**（要不要给），不是**掉落算法**（给什么），两者分层，互不污染。
//
// 红线改写后的表述（待 design-strategist 同步文档）：
//   「回归必有交代」—— 任何一次回归必然收到 ① ≥1 件礼物 **或** ② 一句空手留言，二者必有其一。
//   本文件就是这条新红线的执行点：emptyHanded=true 时必配 EMPTY_HANDS_NOTES 文案（见 sequencer）。
//
// 本文件在 src/domain，**不得**调用 Date.now / Math.random（CI 守门）；rng / now 由调用方注入。
import type { DestinationId, DropResultV1 } from '../../save/types';
import type { DropConfig } from './dropConfig';
import { roll } from './roll';

/** 空手概率（07 §B.1）。名义 0.81；实际受"禁止连续两次空手"保护压到约 0.45（= 0.81/(1+0.81)）。
 *  与明信片解耦后"全无反馈"（既无礼物、也无明信片）概率 ≈ 0.45 × 0.45 ≈ 0.20（20%，老板 2026-09-18 拍板，原 15%/1.5%）。 */
export const EMPTY_HANDS_PROBABILITY = 0.81;

export interface ReturnOutcome {
  /** 本次是否空手而归（礼物数为 0，但必有空手留言保底）。 */
  emptyHanded: boolean;
  /** emptyHanded 时 giftIds 为空数组；否则是 roll() 的正常结果（长度 ≥ 1，红线不变）。 */
  dropResult: DropResultV1;
}

export interface PlanReturnArgs {
  config: DropConfig;
  destination: DestinationId;
  tripCount: number;
  /** 未做过完整旅行（!isFirstTripDone）—— 首旅强制带礼物。 */
  isFirstTrip: boolean;
  /** 上一次回归是否空手 —— 连续两次空手禁止（07 §B.1）。 */
  lastReturnWasEmpty: boolean;
  rng: () => number;
  now: number;
}

export function planReturn(args: PlanReturnArgs): ReturnOutcome {
  const { config, destination, tripCount, isFirstTrip, lastReturnWasEmpty, rng, now } = args;

  // 两条禁止：首旅必须给正反馈；上一次已经空手则这次必须给（连续两次空手是挫败，不是惊喜）。
  const forceGift = isFirstTrip || lastReturnWasEmpty;

  // ⚠️ rng 消耗：比直接调 roll() **多 1 次**（空手判定），且只在非强制带礼物时才消耗。
  //   forceGift 路径下这一抽不消耗 —— 好处是首旅与"上次空手"的 rng 序列与非空手路径一致，
  //   但也意味着两条路径的 rng 序列不同，写脚本化 rng 测试时必须意识到这一点（07 §B-E2 允许的二选一）。
  const emptyHanded = !forceGift && rng() < EMPTY_HANDS_PROBABILITY;

  if (emptyHanded) {
    // 空手：不进 roll()，自然也不会消耗它内部那几次 rng。
    return { emptyHanded: true, dropResult: { giftIds: [], rolledAt: now } };
  }

  return { emptyHanded: false, dropResult: roll(config, destination, tripCount, rng, now) };
}
